import type {
  TradeIdeasColumnPatch,
  TradeIdeasColumnMapping,
  TradeIdeasFilter,
  TradeIdeasFilterCatalogRefresh,
  TradeIdeasFilterCatalogSeed,
  TradeIdeasHealth,
  TradeIdeasOutboxItem,
  TradeIdeasRuntimeConfig,
  TradeIdeasScanner,
  TradeIdeasScannerColumn,
  TradeIdeasSink,
  TradeIdeasStatus,
} from "@/lib/tradeideas/types";

const BASE = "/api/v1/ti-bot";

export class TradeIdeasApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`${status}: ${detail}`);
  }
}

async function unwrap<T>(resp: Response): Promise<T> {
  if (resp.ok) {
    return (await resp.json()) as T;
  }
  let detail = resp.statusText;
  try {
    detail = await resp.text();
  } catch {
    detail = resp.statusText;
  }
  throw new TradeIdeasApiError(resp.status, detail);
}

function url(path: string): string {
  return `${BASE}${path}`;
}

async function get<T>(path: string): Promise<T> {
  const resp = await fetch(url(path), { cache: "no-cache", method: "GET" });
  return unwrap<T>(resp);
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(url(path), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return unwrap<T>(resp);
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const resp = await fetch(url(path), {
    method: "POST",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return unwrap<T>(resp);
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(url(path), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return unwrap<T>(resp);
}

async function del<T>(path: string): Promise<T> {
  const resp = await fetch(url(path), { method: "DELETE" });
  return unwrap<T>(resp);
}

export const tradeIdeasBotApi = {
  health: () => get<TradeIdeasHealth>("/health"),
  status: () => get<TradeIdeasStatus>("/api/v1/status"),
  config: () => get<TradeIdeasRuntimeConfig>("/api/v1/config"),
  scanners: () => get<TradeIdeasScanner[]>("/api/v1/tradeideas/scanners"),
  filters: () => get<TradeIdeasFilter[]>("/api/v1/tradeideas/filters"),
  filterCatalogSeed: () =>
    get<TradeIdeasFilterCatalogSeed>("/api/v1/tradeideas/filter-catalog/seed"),
  refreshFilterCatalog: () =>
    post<TradeIdeasFilterCatalogRefresh>("/api/v1/tradeideas/filter-catalog/refresh"),
  columns: (scannerId: string) =>
    get<TradeIdeasScannerColumn[]>(
      `/api/v1/tradeideas/scanners/${encodeURIComponent(scannerId)}/columns`,
    ),
  columnMappings: (scannerId: string) =>
    get<TradeIdeasColumnMapping[]>(
      `/api/v1/tradeideas/scanners/${encodeURIComponent(scannerId)}/column-mappings`,
    ),
  updateColumn: (
    scannerId: string,
    columnId: number,
    patchBody: TradeIdeasColumnPatch,
  ) =>
    patch<TradeIdeasScannerColumn>(
      `/api/v1/tradeideas/scanners/${encodeURIComponent(scannerId)}/columns/${columnId}`,
      patchBody,
    ),
  updateColumnMapping: (
    scannerId: string,
    columnId: number,
    patchBody: TradeIdeasColumnPatch,
  ) =>
    patch<TradeIdeasColumnMapping>(
      `/api/v1/tradeideas/scanners/${encodeURIComponent(scannerId)}/column-mappings/${columnId}`,
      patchBody,
    ),
  sinks: () => get<TradeIdeasSink[]>("/api/v1/config/delivery/sinks"),
  upsertSink: (name: string, sink: TradeIdeasSink) =>
    put<{ delivery: { sinks: TradeIdeasSink[] }; restart_required: boolean }>(
      `/api/v1/config/delivery/sinks/${encodeURIComponent(name)}`,
      sink,
    ),
  deleteSink: (name: string) =>
    del<{ delivery: { sinks: TradeIdeasSink[] }; restart_required: boolean }>(
      `/api/v1/config/delivery/sinks/${encodeURIComponent(name)}`,
    ),
  outbox: (status: string, limit = 100) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("limit", String(limit));
    return get<TradeIdeasOutboxItem[]>(`/api/v1/outbox?${params.toString()}`);
  },
  retryOutbox: () => post<{ queued: number }>("/api/v1/outbox/retry"),
};
